import React, { useState, useEffect } from 'react';

/**
 * Value component with vertical semi-circle timeline showing our 4-step process
 * Auto-advances every 5 seconds with clickable timeline points
 */
const Value = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(5);
  const [isPaused, setIsPaused] = useState(false);

  // Section heading and description for 'How We Work'
  const sectionHeader = {
    title: 'How We Work',
    description: 'Our proven 4-step process ensures you get the right solution, every time.'
  };

  const steps = [
    {
      number: 1,
      title: "Needs Discovery",
      subtitle: "Understanding Your World",
      icon: "fas fa-search",
      color: "#ff6b6b",
      items: [
        "We dive deep into your workflows so you get software solutions that fit like a glove",
        "No wasted features or costly add‑ons",
        "Tailored specifically to your unique business processes"
      ]
    },
    {
      number: 2,
      title: "Live Proof of Concept",
      subtitle: "See Before You Buy",
      icon: "fas fa-eye",
      color: "#45b7d1",
      items: [
        "Experience a working proof‑of‑concept of your solution on our servers",
        "Complete with all requested features and integrations",
        "Know exactly what you're buying before you invest"
      ]
    },
    {
      number: 3,
      title: "Requirements Sign‑Off & UAT",
      subtitle: "Bringing Ideas to Life",
      icon: "fas fa-clipboard-check",
      color: "#96ceb4",
      items: [
        "Together, we nail down every detail in writing",
        "Cutting scope creep and extra rounds of change requests",
        "Showcase all custom features in action and prove your ROI"
      ]
    },
    {
      number: 4,
      title: "Handoff & Deployment",
      subtitle: "Ready for Success",
      icon: "fas fa-rocket",
      color: "#ffeaa7",
      items: [
        "You get a turnkey, fully customized software solution ready to use",
        "Ready for immediate day‑one productivity and efficiency",
        "No delays, no hidden implementation fees"
      ]
    }
  ];

  // Handle manual step selection
  const handleStepClick = (stepIndex) => {
    setActiveStep(stepIndex);
    setTimeRemaining(5); // Reset timer
    setIsPaused(false);
  };

  // Auto-advance timer
  useEffect(() => {
    if (isPaused) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 0) {
          // Move to next step when timer reaches 0
          setActiveStep((currentStep) => (currentStep + 1) % steps.length);
          return 5; // Reset to 5 seconds
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isPaused, steps.length]);

  // Pause on hover
  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  return (
    <section id="value" className="workflow-section">
      {/* Animated Background */}
      <div className="workflow-bg">
        <div className="workflow-orb workflow-orb-1"></div>
        <div className="workflow-orb workflow-orb-2"></div>
        <div className="workflow-orb workflow-orb-3"></div>
      </div>

      <div className="workflow-content">
        <div className="section-header">
          <h2>How We Work</h2>
          <p>Our proven 4-step process ensures you get systems that actually improve your business operations from day one.</p>
        </div>

        <div className="workflow-container">
          {/* Main Content - Left Side */}
          <div className="workflow-main">
            <div
              className="workflow-card"
              key={activeStep}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {/* Timer Circle */}
              <div className="timer-circle">
                <svg width="40" height="40" className="timer-svg">
                  <circle
                    cx="20"
                    cy="20"
                    r="18"
                    fill="none"
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth="2"
                  />
                  <circle
                    cx="20"
                    cy="20"
                    r="18"
                    fill="none"
                    stroke={steps[activeStep].color}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeDasharray="113"
                    strokeDashoffset={113 - (113 * (5 - timeRemaining)) / 5}
                    className="timer-progress"
                  />
                </svg>
              </div>

              <div className="workflow-icon" style={{ background: steps[activeStep].color }}>
                <i className={steps[activeStep].icon}></i>
              </div>

              <div className="workflow-number">
                Step {steps[activeStep].number}
              </div>

              <h3 className="workflow-title">
                {steps[activeStep].title}
              </h3>

              <p className="workflow-subtitle">
                {steps[activeStep].subtitle}
              </p>

              <ul className="workflow-items">
                {steps[activeStep].items.map((item, index) => (
                  <li key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                    <i className="fas fa-check-circle"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Semi-Circle Timeline - Right Side */}
          <div className="workflow-timeline">
            <div className="timeline-track"></div>
            {steps.map((step, index) => (
              <div
                key={index}
                className={`timeline-step ${activeStep === index ? 'active' : ''} ${activeStep > index ? 'completed' : ''}`}
                style={{
                  '--step-color': step.color,
                  '--step-index': index,
                  '--total-steps': steps.length
                }}
                onClick={() => handleStepClick(index)}
              >
                <div className="step-circle">
                  <i className={step.icon}></i>
                  <span className="step-num">{step.number}</span>
                </div>
                <div className="step-connector"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Value;